/**
 * FacultyDetailPage - Admin detail view for a single faculty member
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getAdminFacultyMember,
  updateFacultyStatus,
  updateFacultyPayRate,
  addFacultyShowCertification,
  removeFacultyShowCertification,
  setFacultyShowActive,
  resendFacultyInvitation,
  sendFacultyStripeReminder,
  overrideActivateFaculty,
  getShows,
  getFacultyAssignments,
  getFacultyAvailability,
  addFacultyAvailabilityBlock,
  removeFacultyAvailabilityBlock,
  getAdminFacultyPayouts,
  issueFacultyPayment,
  DuplicatePaymentWarning,
  type AdminFaculty,
  type FacultyPayoutAdmin,
  type Show,
  type ClassAssignment,
  type AvailabilityBlock,
  type AvailabilityAssignment,
} from '../../../api/admin';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import { parseLocalDate } from '../../../utils/date';
import { formatTimeDisplay } from '@sda/shared';

export default function FacultyDetailPage() {
  const { id } = useParams<{ id: string }>();

  const [faculty, setFaculty] = useState<AdminFaculty | null>(null);
  const [shows, setShows] = useState<Show[]>([]);
  const [assignments, setAssignments] = useState<ClassAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Modals
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showPayRateModal, setShowPayRateModal] = useState(false);
  const [newPayRate, setNewPayRate] = useState('');
  const [showAddShowModal, setShowAddShowModal] = useState(false);
  const [selectedShowId, setSelectedShowId] = useState('');
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    variant: 'danger' | 'primary';
    action: () => Promise<void> | void;
  } | null>(null);

  // Active tab
  const [activeTab, setActiveTab] = useState<'profile' | 'assignments' | 'availability' | 'payouts'>('profile');

  // Availability state
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [availBlocks, setAvailBlocks] = useState<AvailabilityBlock[]>([]);
  const [availAssignments, setAvailAssignments] = useState<AvailabilityAssignment[]>([]);
  const [isLoadingAvail, setIsLoadingAvail] = useState(false);
  const [showAddBlockModal, setShowAddBlockModal] = useState(false);
  const [blockStartDate, setBlockStartDate] = useState('');
  const [blockEndDate, setBlockEndDate] = useState('');
  const [blockReason, setBlockReason] = useState('');

  // Payouts tab (SDA-221)
  const [payoutsList, setPayoutsList] = useState<FacultyPayoutAdmin[]>([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutsLoaded, setPayoutsLoaded] = useState(false);
  const [payoutsError, setPayoutsError] = useState<string | null>(null);
  const [showIssuePaymentModal, setShowIssuePaymentModal] = useState(false);
  const [issueAmount, setIssueAmount] = useState('');
  const [issueType, setIssueType] = useState<'reimbursement' | 'bonus' | 'other'>('reimbursement');
  const [issueNote, setIssueNote] = useState('');
  const [issueSubmitting, setIssueSubmitting] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [issueDupWarning, setIssueDupWarning] = useState<string | null>(null);
  const [issueConfirmOpen, setIssueConfirmOpen] = useState(false);

  useEffect(() => {
    if (id) {
      loadFaculty(id);
      loadShows();
      loadAssignments(id);
    }
  }, [id]);

  // Auto-dismiss success message
  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  // Load availability when tab is active or month changes
  useEffect(() => {
    if (id && activeTab === 'availability') {
      loadAvailability(id);
    }
  }, [id, activeTab, calendarMonth]);

  // Load payout history when the payouts tab is first activated (SDA-221)
  useEffect(() => {
    if (id && activeTab === 'payouts' && !payoutsLoaded && !payoutsLoading) {
      loadPayouts(id);
    }
  }, [id, activeTab, payoutsLoaded, payoutsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFaculty = async (facultyId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAdminFacultyMember(facultyId);
      setFaculty(data);
      if (data.pay_rate_cents) {
        setNewPayRate((data.pay_rate_cents / 100).toString());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load faculty');
    } finally {
      setIsLoading(false);
    }
  };

  const loadShows = async () => {
    try {
      const showsData = await getShows();
      setShows(showsData);
    } catch (err) {
      console.error('Failed to load shows:', err);
    }
  };

  const loadAssignments = async (facultyId: string) => {
    try {
      const data = await getFacultyAssignments(facultyId);
      setAssignments(data);
    } catch (err) {
      console.error('Failed to load assignments:', err);
    }
  };

  const loadAvailability = async (facultyId: string) => {
    try {
      setIsLoadingAvail(true);
      const startDate = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1).toISOString().split('T')[0]!;
      const endDate = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).toISOString().split('T')[0]!;
      const data = await getFacultyAvailability(facultyId, startDate, endDate);
      setAvailBlocks(data.availability_blocks || []);
      setAvailAssignments(data.assignments || []);
    } catch (err) {
      console.error('Failed to load availability:', err);
    } finally {
      setIsLoadingAvail(false);
    }
  };

  const handleAddBlock = async () => {
    if (!id || !blockStartDate || !blockEndDate) return;
    try {
      setIsSaving(true);
      await addFacultyAvailabilityBlock(id, {
        start_date: blockStartDate,
        end_date: blockEndDate,
        reason: blockReason.trim() || undefined,
      });
      setShowAddBlockModal(false);
      setBlockStartDate('');
      setBlockEndDate('');
      setBlockReason('');
      await loadAvailability(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add blocked dates');
    } finally {
      setIsSaving(false);
    }
  };

  const loadPayouts = async (facultyId: string) => {
    try {
      setPayoutsLoading(true);
      setPayoutsError(null);
      const result = await getAdminFacultyPayouts({ faculty_id: facultyId, per_page: 50 });
      setPayoutsList(result.payouts);
      setPayoutsLoaded(true);
    } catch (err) {
      setPayoutsError(err instanceof Error ? err.message : 'Failed to load payouts');
    } finally {
      setPayoutsLoading(false);
    }
  };

  const issueAmountCents = Math.round(parseFloat(issueAmount || '0') * 100);
  const issueFormValid = issueAmountCents >= 100 && issueNote.trim().length >= 3;
  const stripeReady = faculty?.stripe_account_status === 'enabled';

  const closeIssuePaymentModal = () => {
    setShowIssuePaymentModal(false);
    setIssueAmount('');
    setIssueType('reimbursement');
    setIssueNote('');
    setIssueError(null);
    setIssueDupWarning(null);
    setIssueConfirmOpen(false);
  };

  const handleIssuePayment = async () => {
    if (!id || !faculty) return;
    try {
      setIssueSubmitting(true);
      setIssueError(null);
      const result = await issueFacultyPayment(id, {
        amount_cents: issueAmountCents,
        payment_type: issueType,
        note: issueNote.trim(),
        force: !!issueDupWarning,
      });
      setIssueDupWarning(null);
      if (result.processed) {
        setSuccessMessage(`Payment of ${formatCurrency(issueAmountCents)} sent to ${faculty.name}.`);
        closeIssuePaymentModal();
      } else {
        setIssueError(`Payment recorded but the transfer failed: ${result.error || 'Unknown error'}`);
      }
      await loadPayouts(id);
    } catch (err) {
      if (err instanceof DuplicatePaymentWarning) {
        setIssueDupWarning(err.message);
      } else {
        setIssueError(err instanceof Error ? err.message : 'Failed to issue payment');
      }
    } finally {
      setIssueSubmitting(false);
    }
  };

  const handleRemoveBlock = async (blockId: string) => {
    if (!id) return;
    try {
      await removeFacultyAvailabilityBlock(id, blockId);
      await loadAvailability(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove blocked dates');
    }
  };

  const handleApprove = async () => {
    if (!id) return;

    try {
      setIsSaving(true);
      await updateFacultyStatus(id, 'active');
      await loadFaculty(id);
      setShowApproveModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve faculty');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverrideActivate = async () => {
    if (!id) return;

    try {
      setIsSaving(true);
      await overrideActivateFaculty(id);
      await loadFaculty(id);
      setShowOverrideModal(false);
      setSuccessMessage('Faculty activated. They are now bookable to classes.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to override-activate faculty');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReject = async () => {
    if (!id) return;

    try {
      setIsSaving(true);
      await updateFacultyStatus(id, 'inactive');
      await loadFaculty(id);
      setShowRejectModal(false);
      setRejectReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject faculty');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdatePayRate = async () => {
    if (!id || !newPayRate) return;

    try {
      setIsSaving(true);
      const cents = Math.round(parseFloat(newPayRate) * 100);
      await updateFacultyPayRate(id, cents);
      await loadFaculty(id);
      setShowPayRateModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update pay rate');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddShow = async () => {
    if (!id || !selectedShowId) return;

    try {
      setIsSaving(true);
      await addFacultyShowCertification(id, selectedShowId);
      await loadFaculty(id);
      setShowAddShowModal(false);
      setSelectedShowId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add show certification');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveShow = async (showId: string) => {
    if (!id) return;

    try {
      await removeFacultyShowCertification(id, showId);
      await loadFaculty(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove show certification');
    }
  };

  const handleToggleShowActive = async (showId: string, nextActive: boolean) => {
    if (!id || !faculty) return;

    const prev = faculty;
    // Optimistic: flip the cert's active flag in place — no full reload, no blank screen.
    setFaculty({
      ...faculty,
      shows: faculty.shows?.map((s) =>
        s.id === showId ? { ...s, cert_active: nextActive } : s
      ),
    });

    try {
      await setFacultyShowActive(id, showId, nextActive);
      setSuccessMessage(nextActive ? 'Show marked active' : 'Show marked inactive');
    } catch (err) {
      setFaculty(prev); // revert on failure
      setError(err instanceof Error ? err.message : 'Failed to update show certification');
    }
  };

  const handleResendInvitation = async () => {
    if (!id || !faculty) return;

    try {
      setIsSaving(true);
      await resendFacultyInvitation(id);
      setError(null);
      setSuccessMessage(`Invitation sent to ${faculty.email}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend invitation');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendStripeReminder = async () => {
    if (!id || !faculty) return;

    try {
      setIsSaving(true);
      await sendFacultyStripeReminder(id);
      setError(null);
      setSuccessMessage(`Stripe setup reminder sent to ${faculty.email}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send Stripe reminder');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!id) return;
    if (!confirm('Are you sure you want to deactivate this faculty member?')) return;

    try {
      await updateFacultyStatus(id, 'inactive');
      await loadFaculty(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate faculty');
    }
  };

  const handleReactivate = async () => {
    if (!id) return;

    try {
      await updateFacultyStatus(id, 'active');
      await loadFaculty(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reactivate faculty');
    }
  };

  // Onboarding progress: 4 steps (W-9 handled by Stripe Connect)
  const getOnboardingProgress = (faculty: AdminFaculty) => {
    const steps = [
      { name: 'Profile Photo', completed: !!faculty.headshot_url, key: 'photo' },
      { name: 'Bio', completed: !!(faculty.bio && faculty.bio.length >= 100), key: 'bio' },
      { name: 'Payment & Tax Setup', completed: faculty.stripe_connect_status === 'enabled', key: 'stripe' }, // Stripe also collects W-9 tax info
      { name: 'Show Certifications', completed: !!(faculty.shows && faculty.shows.length > 0), key: 'shows' },
    ];
    const completed = steps.filter((s) => s.completed).length;
    return { steps, completed, total: 4, percentage: Math.round((completed / 4) * 100) };
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDate = (date: string) => {
    // parseLocalDate appends T12:00:00 for date-only strings (no 'T') so they
    // display as the correct calendar day in all timezones.  Full ISO timestamps
    // (created_at etc.) already carry an offset and pass through unchanged.
    return parseLocalDate(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const paymentTypeLabel = (type: string) =>
    type === 'reimbursement' ? 'Reimbursement'
    : type === 'bonus' ? 'Bonus'
    : 'One-off payment';

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      invited: 'bg-purple-100 text-purple-700',
      onboarding: 'bg-primary-100 text-primary-700',
      pending_approval: 'bg-warning-100 text-warning-700',
      active: 'bg-success-100 text-success-700',
      inactive: 'bg-warm-100 text-warm-600',
    };
    const labels: Record<string, string> = {
      invited: 'Invited',
      onboarding: 'Onboarding',
      pending_approval: 'Pending Approval',
      active: 'Active',
      inactive: 'Inactive',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || 'bg-warm-100 text-warm-600'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getAssignmentStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-warning-100 text-warning-700',
      accepted: 'bg-success-100 text-success-700',
      scheduled: 'bg-primary-100 text-primary-700',
      completed: 'bg-warm-100 text-warm-600',
      declined: 'bg-danger-100 text-danger-700',
      cancelled: 'bg-danger-50 text-danger-500',
    };
    const labels: Record<string, string> = {
      pending: 'Pending',
      accepted: 'Accepted',
      scheduled: 'Scheduled',
      completed: 'Completed',
      declined: 'Declined',
      cancelled: 'Cancelled',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-warm-100 text-warm-600'}`}>
        {labels[status] || status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (isLoading && !faculty) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-warm-200 rounded w-1/4 mb-4" />
        <div className="h-4 bg-warm-200 rounded w-1/2 mb-8" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
            <div className="h-32 bg-warm-100 rounded mb-4" />
            <div className="h-6 bg-warm-100 rounded w-1/3 mb-2" />
            <div className="h-4 bg-warm-100 rounded w-2/3" />
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="h-6 bg-warm-100 rounded w-1/2 mb-4" />
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-4 bg-warm-100 rounded" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !faculty) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded-lg p-6 text-center">
        <p className="text-danger-700">{error}</p>
        <button
          onClick={() => id && loadFaculty(id)}
          className="mt-4 px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!faculty) return null;

  const progress = getOnboardingProgress(faculty);
  const availableShows = shows.filter(
    (show) => !faculty.shows?.some((fs) => fs.id === show.id)
  );

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-start gap-4">
          <Link
            to="/admin/faculty"
            className="text-warm-600 hover:text-warm-900 transition-colors self-start"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>

          {faculty.headshot_url ? (
            <img
              src={faculty.headshot_url}
              alt={faculty.name}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-warm-200 flex items-center justify-center">
              <span className="text-2xl text-warm-600 font-medium">
                {faculty.name?.charAt(0) || '?'}
              </span>
            </div>
          )}

          <div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">
                {faculty.name}
              </h1>
              {getStatusBadge(faculty.status)}
            </div>
            <p className="text-warm-600">{faculty.email}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {faculty.status === 'pending_approval' && (
            <>
              <button
                onClick={() => setShowRejectModal(true)}
                className="px-3 py-1.5 text-sm border border-danger-300 text-danger-700 rounded-lg hover:bg-danger-50 transition-colors"
              >
                Reject
              </button>
              <button
                onClick={() => setShowApproveModal(true)}
                className="px-3 py-1.5 text-sm bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors"
              >
                Approve
              </button>
            </>
          )}

          {(faculty.status === 'invited' || faculty.status === 'onboarding' || faculty.status === 'pending_approval') && (
            <button
              onClick={() => setShowOverrideModal(true)}
              disabled={isSaving}
              className="px-3 py-1.5 text-sm border border-warning-300 text-warning-700 rounded-lg hover:bg-warning-50 disabled:opacity-50 transition-colors"
            >
              Override Activate
            </button>
          )}

          {!faculty.invitation_accepted_at && (faculty.status === 'invited' || faculty.status === 'onboarding' || !faculty.user_id) && (
            <button
              onClick={() =>
                setConfirmAction({
                  title: faculty.status === 'onboarding' ? 'Resend setup link' : 'Resend invitation',
                  message: `Resend the invitation email to ${faculty.email}?`,
                  confirmLabel: faculty.status === 'onboarding' ? 'Resend Setup Link' : 'Resend Invitation',
                  variant: 'primary',
                  action: handleResendInvitation,
                })
              }
              disabled={isSaving}
              className="px-3 py-1.5 text-sm border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 transition-colors"
            >
              {isSaving ? 'Sending...' : faculty.status === 'onboarding' ? 'Resend Setup Link' : 'Resend Invitation'}
            </button>
          )}

          {faculty.status === 'active' && (
            <button
              onClick={handleDeactivate}
              className="px-3 py-1.5 text-sm border border-danger-300 text-danger-700 rounded-lg hover:bg-danger-50 transition-colors"
            >
              Deactivate
            </button>
          )}

          {faculty.status === 'inactive' && (
            <button
              onClick={handleReactivate}
              className="px-3 py-1.5 text-sm bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors"
            >
              Reactivate
            </button>
          )}

          <Link
            to={`/admin/faculty/${id}/edit`}
            className="btn-primary text-sm"
          >
            Edit Profile
          </Link>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg text-success-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-success-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {successMessage}
          </div>
          <button onClick={() => setSuccessMessage(null)} className="text-success-500 hover:text-success-700">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-warm-200 mb-6 overflow-x-auto">
        <nav className="flex gap-8 min-w-max">
          {[
            { id: 'profile', label: 'Profile' },
            { id: 'assignments', label: 'Assignments' },
            { id: 'availability', label: 'Availability' },
            { id: 'payouts', label: 'Payouts' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`pb-4 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-warm-600 hover:text-warm-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Bio */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">Bio</h2>
              {faculty.bio ? (
                <p className="text-warm-700 whitespace-pre-wrap">{faculty.bio}</p>
              ) : (
                <p className="text-warm-400 italic">No bio provided yet.</p>
              )}

              {faculty.broadway_credits && faculty.broadway_credits.length > 0 && (
                <div className="mt-6 pt-6 border-t border-warm-100">
                  <h3 className="font-medium text-warm-900 mb-3">Broadway Credits</h3>
                  <div className="space-y-2">
                    {faculty.broadway_credits.map((credit, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-warm-900 font-medium">{credit.show}</span>
                        <span className="text-warm-600">
                          {credit.role} ({credit.year})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Show Certifications */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-warm-900">Show Certifications</h2>
                <button
                  onClick={() => setShowAddShowModal(true)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  + Add Show
                </button>
              </div>

              {faculty.shows && faculty.shows.length > 0 ? (
                <div className="space-y-3">
                  {faculty.shows.map((show) => {
                    const isInactive = show.cert_active === false;
                    return (
                      <div
                        key={show.id}
                        className={`flex items-center justify-between p-3 bg-warm-50 rounded-lg ${
                          isInactive ? 'opacity-60' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {show.logo_url && (
                            <img
                              src={show.logo_url}
                              alt={show.name}
                              className="w-8 h-8 rounded object-cover"
                            />
                          )}
                          <span className="font-medium text-warm-900">{show.name}</span>
                          {isInactive && (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-warm-200 text-warm-600">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => handleToggleShowActive(show.id, isInactive)}
                            className="text-sm text-primary-600 hover:text-primary-700"
                          >
                            {isInactive ? 'Set Active' : 'Set Inactive'}
                          </button>
                          <button
                            onClick={() =>
                              setConfirmAction({
                                title: 'Remove certification',
                                message: `Remove the "${show.name}" certification from this faculty member?`,
                                confirmLabel: 'Remove',
                                variant: 'danger',
                                action: () => handleRemoveShow(show.id),
                              })
                            }
                            className="text-sm text-danger-600 hover:text-danger-700"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-warm-400 italic">No show certifications yet.</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Onboarding Progress */}
            {(faculty.status === 'onboarding' || faculty.status === 'pending_approval') && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-warm-900 mb-4">
                  Onboarding Progress
                </h2>

                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-warm-600">Progress</span>
                    <span className="font-medium text-warm-900">
                      {progress.completed}/{progress.total} complete
                    </span>
                  </div>
                  <div className="w-full h-2 bg-warm-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all"
                      style={{ width: `${progress.percentage}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  {progress.steps.map((step) => (
                    <div key={step.key} className="flex items-center gap-3">
                      {step.completed ? (
                        <svg className="w-5 h-5 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-warm-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      <span className={step.completed ? 'text-warm-900' : 'text-warm-500'}>
                        {step.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pay Rate */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-warm-900">Pay Rate</h2>
                <button
                  onClick={() => setShowPayRateModal(true)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Edit
                </button>
              </div>

              <p className="text-3xl font-bold text-warm-900">
                {faculty.pay_rate_cents
                  ? formatCurrency(faculty.pay_rate_cents)
                  : '--'}
              </p>
              <p className="text-warm-500">per class</p>
            </div>

            {/* Stripe Connect Status */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Payment Setup
              </h2>

              {faculty.stripe_connect_id ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    {faculty.stripe_connect_status === 'enabled' ? (
                      <svg className="w-5 h-5 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-warning-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    <span className="font-medium text-warm-900">
                      Stripe Connect: {faculty.stripe_connect_status}
                    </span>
                  </div>

                  <p className="text-xs text-warm-500 mt-2">
                    Tax documents (W-9) are collected by Stripe during onboarding.
                  </p>
                  {faculty.stripe_connect_status !== 'enabled' && (
                    <>
                      <button
                        onClick={handleSendStripeReminder}
                        disabled={isSaving || !faculty.user_id}
                        title={!faculty.user_id ? 'This faculty member has no login account yet.' : undefined}
                        className="w-full px-4 py-2 text-sm border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 disabled:opacity-50 transition-colors mt-2"
                      >
                        {isSaving ? 'Sending...' : 'Send Stripe Setup Reminder'}
                      </button>
                      {!faculty.user_id && (
                        <p className="text-xs text-warm-500 mt-1">
                          No login account yet — resend their invitation first.
                        </p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-warm-400 italic">Not connected yet.</p>
                  <button
                    onClick={handleSendStripeReminder}
                    disabled={isSaving || !faculty.user_id}
                    title={!faculty.user_id ? 'This faculty member has no login account yet.' : undefined}
                    className="w-full px-4 py-2 text-sm border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 disabled:opacity-50 transition-colors"
                  >
                    {isSaving ? 'Sending...' : 'Send Stripe Setup Reminder'}
                  </button>
                  {!faculty.user_id && (
                    <p className="text-xs text-warm-500">
                      No login account yet — resend their invitation first.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Contact Information
              </h2>

              <div className="space-y-3">
                <div>
                  <p className="text-sm text-warm-500">Email</p>
                  <a
                    href={`mailto:${faculty.email}`}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    {faculty.email}
                  </a>
                </div>
                {faculty.phone && (
                  <div>
                    <p className="text-sm text-warm-500">Phone</p>
                    <a
                      href={`tel:${faculty.phone}`}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      {faculty.phone}
                    </a>
                  </div>
                )}
                <div>
                  <p className="text-sm text-warm-500">Joined</p>
                  <p className="text-warm-900">{formatDate(faculty.created_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'assignments' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-warm-100">
            <h2 className="text-lg font-semibold text-warm-900">Class Assignments</h2>
          </div>

          {assignments.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-warm-500">No assignments yet.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-warm-50">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-warm-700">
                    Class
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-warm-700">
                    Studio
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-warm-700">
                    Date & Time
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-warm-700">
                    Status
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-warm-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-warm-100">
                {assignments.map((assignment) => (
                  <tr key={assignment.id} className="hover:bg-warm-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-warm-900">
                        {assignment.show?.name}
                      </p>
                      <p className="text-sm text-warm-500">
                        {assignment.city?.name}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-warm-900">{assignment.studio?.name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-warm-900">
                        {formatDate(assignment.scheduled_date)}
                      </p>
                      <p className="text-sm text-warm-500">
                        {assignment.start_time ? formatTimeDisplay(assignment.start_time) : '—'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      {getAssignmentStatusBadge(assignment.status)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/admin/bookings/${assignment.booking_id}`}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        View Booking
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'availability' && (() => {
        const year = calendarMonth.getFullYear();
        const month = calendarMonth.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const monthLabel = calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

        // Build lookup sets for blocked and assigned dates
        const blockedDates = new Set<string>();
        availBlocks.forEach((b) => {
          const start = new Date(b.start_date + 'T00:00:00');
          const end = new Date(b.end_date + 'T00:00:00');
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            blockedDates.add(d.toISOString().split('T')[0]!);
          }
        });

        const assignedDates = new Map<string, AvailabilityAssignment[]>();
        availAssignments.forEach((a) => {
          const dateKey = a.class_date?.slice(0, 10);
          if (dateKey) {
            if (!assignedDates.has(dateKey)) assignedDates.set(dateKey, []);
            assignedDates.get(dateKey)!.push(a);
          }
        });

        const days: (number | null)[] = [];
        for (let i = 0; i < firstDay; i++) days.push(null);
        for (let d = 1; d <= daysInMonth; d++) days.push(d);

        return (
          <div className="space-y-6">
            {/* Calendar */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-warm-900">Availability Calendar</h2>
                <button
                  onClick={() => setShowAddBlockModal(true)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                >
                  + Block Dates
                </button>
              </div>

              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setCalendarMonth(new Date(year, month - 1, 1))}
                  className="p-2 hover:bg-warm-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-warm-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h3 className="text-lg font-semibold text-warm-900">{monthLabel}</h3>
                <button
                  onClick={() => setCalendarMonth(new Date(year, month + 1, 1))}
                  className="p-2 hover:bg-warm-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-warm-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {isLoadingAvail ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : (
                <>
                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                      <div key={d} className="text-center text-xs font-semibold text-warm-500 py-2">{d}</div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {days.map((day, i) => {
                      if (day === null) return <div key={`empty-${i}`} />;
                      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const isBlocked = blockedDates.has(dateStr);
                      const dayAssignments = assignedDates.get(dateStr);
                      const isToday = dateStr === new Date().toISOString().split('T')[0];

                      return (
                        <div
                          key={day}
                          className={`relative p-2 min-h-[60px] rounded-lg border text-sm transition-colors ${
                            isBlocked
                              ? 'bg-danger-50 border-danger-200'
                              : dayAssignments
                                ? 'bg-primary-50 border-primary-200'
                                : 'bg-white border-warm-100 hover:border-warm-200'
                          } ${isToday ? 'ring-2 ring-primary-400' : ''}`}
                        >
                          <span className={`text-xs font-medium ${
                            isBlocked ? 'text-danger-700' : isToday ? 'text-primary-700' : 'text-warm-700'
                          }`}>
                            {day}
                          </span>
                          {isBlocked && (
                            <div className="mt-0.5">
                              <span className="text-[10px] text-danger-600 font-medium">Blocked</span>
                            </div>
                          )}
                          {dayAssignments && dayAssignments.map((a, idx) => (
                            <div key={idx} className="mt-0.5">
                              <span className="text-[10px] text-primary-700 font-medium truncate block">
                                {(a.tour_stops as any)?.shows?.name || 'Class'}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div className="flex flex-wrap items-center gap-4 sm:gap-6 mt-4 pt-4 border-t border-warm-100">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-danger-50 border border-danger-200" />
                      <span className="text-xs text-warm-600">Blocked</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-primary-50 border border-primary-200" />
                      <span className="text-xs text-warm-600">Has Assignment</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-white border border-warm-100" />
                      <span className="text-xs text-warm-600">Available</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-white ring-2 ring-primary-400" />
                      <span className="text-xs text-warm-600">Today</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Blocked Date Ranges */}
            {availBlocks.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-semibold text-warm-900 mb-4">Blocked Date Ranges</h3>
                <div className="space-y-2">
                  {availBlocks.map((block) => (
                    <div
                      key={block.id}
                      className="flex items-center justify-between p-3 bg-danger-50 border border-danger-100 rounded-lg"
                    >
                      <div>
                        <span className="text-warm-900 font-medium">
                          {formatDate(block.start_date)} — {formatDate(block.end_date)}
                        </span>
                        {block.reason && (
                          <span className="text-sm text-warm-500 ml-3">{block.reason}</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveBlock(block.id)}
                        className="text-danger-600 hover:text-danger-700 text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Assignments */}
            {availAssignments.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-semibold text-warm-900 mb-4">Assignments This Month</h3>
                <div className="space-y-2">
                  {availAssignments.map((a) => (
                    <div key={a.id} className="flex items-center justify-between p-3 bg-primary-50 border border-primary-100 rounded-lg">
                      <div>
                        <span className="text-warm-900 font-medium">
                          {formatDate(a.class_date)}
                        </span>
                        <span className="text-sm text-warm-600 ml-3">
                          {(a.tour_stops as any)?.shows?.name || 'Class'}
                        </span>
                        {(a.bookings as any)?.studios?.name && (
                          <span className="text-sm text-warm-500 ml-2">
                            @ {(a.bookings as any).studios.name}
                          </span>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        a.status === 'scheduled' ? 'bg-primary-100 text-primary-700' :
                        a.status === 'completed' ? 'bg-warm-100 text-warm-600' :
                        a.status === 'cancelled' ? 'bg-danger-50 text-danger-500' :
                        'bg-warning-100 text-warning-700'
                      }`}>
                        {a.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Block Modal */}
            {showAddBlockModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 overflow-y-auto max-h-[90vh]">
                  <h3 className="text-lg font-semibold text-warm-900 mb-4">Block Dates</h3>
                  <p className="text-sm text-warm-600 mb-4">
                    Mark dates when this faculty member is unavailable.
                  </p>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-warm-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={blockStartDate}
                          onChange={(e) => {
                            setBlockStartDate(e.target.value);
                            if (!blockEndDate || e.target.value > blockEndDate) setBlockEndDate(e.target.value);
                          }}
                          className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-warm-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={blockEndDate}
                          min={blockStartDate}
                          onChange={(e) => setBlockEndDate(e.target.value)}
                          className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-warm-700 mb-1">Reason (optional)</label>
                      <input
                        type="text"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        placeholder="e.g., Vacation, Personal, Other show"
                        className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 justify-end mt-6">
                    <button
                      onClick={() => { setShowAddBlockModal(false); setBlockStartDate(''); setBlockEndDate(''); setBlockReason(''); }}
                      className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddBlock}
                      disabled={isSaving || !blockStartDate || !blockEndDate}
                      className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50 transition-colors font-medium"
                    >
                      {isSaving ? 'Saving...' : 'Block Dates'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {activeTab === 'payouts' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-warm-900">
              Payout History
            </h2>
            <div className="flex flex-col items-start sm:items-end gap-1">
              <button
                onClick={() => setShowIssuePaymentModal(true)}
                disabled={!stripeReady}
                className="btn-primary min-h-[44px] flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                Issue Payment
              </button>
              {!stripeReady && (
                <p className="text-xs text-warm-500">
                  Available once this faculty member completes Stripe onboarding.
                </p>
              )}
            </div>
          </div>

          {payoutsError && (
            <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
              {payoutsError}
            </div>
          )}

          {payoutsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-warm-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : payoutsList.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-warm-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-warm-700">
                      Date
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-warm-700">
                      Description
                    </th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-warm-700">
                      Amount
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-warm-700">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-warm-100">
                  {payoutsList.map((payout) => (
                    <tr key={payout.id}>
                      <td className="px-4 py-3 text-warm-900 whitespace-nowrap">
                        {formatDate(payout.created_at)}
                      </td>
                      <td className="px-4 py-3 text-warm-700">
                        {payout.class_id === null
                          ? `${paymentTypeLabel(payout.payment_type)}${payout.note ? ` — ${payout.note}` : ''}`
                          : [payout.show_name, payout.studio_name].filter(Boolean).join(' – ') || 'Class payout'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-warm-900 whitespace-nowrap">
                        {formatCurrency(payout.amount_cents)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          payout.status === 'paid'
                            ? 'bg-success-100 text-success-700'
                            : payout.status === 'failed'
                            ? 'bg-danger-100 text-danger-700'
                            : payout.status === 'cancelled'
                            ? 'bg-warm-200 text-warm-600'
                            : 'bg-warning-100 text-warning-700'
                        }`}>
                          {payout.status}
                        </span>
                        {payout.status === 'failed' && payout.failure_reason && (
                          <p className="mt-1 text-xs text-danger-600" title={payout.failure_reason}>
                            {payout.failure_reason.length > 60
                              ? payout.failure_reason.slice(0, 60) + '...'
                              : payout.failure_reason}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-warm-500">No payouts yet.</p>
          )}

          {/* Issue Payment Modal (SDA-221) */}
          {showIssuePaymentModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 overflow-y-auto max-h-[90vh]">
                <h3 className="text-lg font-semibold text-warm-900 mb-1">Issue Payment</h3>
                <p className="text-sm text-warm-600 mb-4">
                  Send a one-off payment to <strong>{faculty.name}</strong>'s connected Stripe account.
                </p>

                {issueError && (
                  <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
                    {issueError}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-warm-700 mb-1">
                      Amount <span className="text-danger-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-500">$</span>
                      <input
                        type="number"
                        value={issueAmount}
                        onChange={(e) => { setIssueAmount(e.target.value); setIssueDupWarning(null); }}
                        min="1"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full pl-8 pr-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-700 mb-1">
                      Type <span className="text-danger-500">*</span>
                    </label>
                    <select
                      value={issueType}
                      onChange={(e) => { setIssueType(e.target.value as 'reimbursement' | 'bonus' | 'other'); setIssueDupWarning(null); }}
                      className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="reimbursement">Reimbursement</option>
                      <option value="bonus">Bonus</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-700 mb-1">
                      Reason <span className="text-danger-500">*</span>
                    </label>
                    <textarea
                      value={issueNote}
                      onChange={(e) => setIssueNote(e.target.value)}
                      rows={3}
                      maxLength={500}
                      placeholder="e.g., Travel reimbursement for Chicago masterclass"
                      className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-xs text-warm-500 mt-1">Shown to the faculty member in their payment notification.</p>
                  </div>
                </div>

                {issueDupWarning && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                    <p className="font-semibold mb-1">Possible duplicate</p>
                    <p>{issueDupWarning}</p>
                    <p className="mt-1 text-amber-600">Click "Send Anyway" if this is intentional.</p>
                  </div>
                )}

                <div className="flex gap-3 justify-end mt-6">
                  <button
                    onClick={closeIssuePaymentModal}
                    disabled={issueSubmitting}
                    className="px-4 py-2 min-h-[44px] border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setIssueConfirmOpen(true)}
                    disabled={issueSubmitting || !issueFormValid}
                    className="btn-primary min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {issueSubmitting ? 'Sending...' : issueDupWarning ? 'Send Anyway' : 'Send Payment'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {issueConfirmOpen && (
            <ConfirmModal
              title="Send this payment?"
              message={
                <>
                  Send <strong>{formatCurrency(issueAmountCents)}</strong> to <strong>{faculty.name}</strong>?
                  This immediately transfers funds from the SDA Stripe balance and can't be undone.
                </>
              }
              confirmLabel="Send Payment"
              confirmVariant="primary"
              onConfirm={() => {
                setIssueConfirmOpen(false);
                handleIssuePayment();
              }}
              onCancel={() => setIssueConfirmOpen(false)}
            />
          )}
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-y-auto max-h-[90vh]">
            <div className="p-6 border-b border-warm-100">
              <h2 className="text-xl font-semibold text-warm-900">Approve Faculty</h2>
            </div>
            <div className="p-6">
              <p className="text-warm-600 mb-4">
                Are you sure you want to approve <strong>{faculty.name}</strong>?
                They will be able to receive class assignments.
              </p>
              <div className="bg-success-50 rounded-lg p-4">
                <p className="text-sm text-success-700">
                  Onboarding progress: {progress.completed}/{progress.total} complete
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-warm-100 flex justify-end gap-3">
              <button
                onClick={() => setShowApproveModal(false)}
                className="px-4 py-2 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={isSaving}
                className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 transition-colors"
              >
                {isSaving ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-y-auto max-h-[90vh]">
            <div className="p-6 border-b border-warm-100">
              <h2 className="text-xl font-semibold text-warm-900">Reject Faculty</h2>
            </div>
            <div className="p-6">
              <p className="text-warm-600 mb-4">
                Please provide a reason for rejecting <strong>{faculty.name}</strong>.
                This will be recorded in the system.
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Reason for rejection..."
              />
            </div>
            <div className="p-6 border-t border-warm-100 flex justify-end gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={isSaving || !rejectReason}
                className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50 transition-colors"
              >
                {isSaving ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Rate Modal */}
      {showPayRateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-y-auto max-h-[90vh]">
            <div className="p-6 border-b border-warm-100">
              <h2 className="text-xl font-semibold text-warm-900">Update Pay Rate</h2>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-warm-700 mb-1">
                Pay Rate (per class)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-500">
                  $
                </span>
                <input
                  type="number"
                  value={newPayRate}
                  onChange={(e) => setNewPayRate(e.target.value)}
                  min="0"
                  step="0.01"
                  className="w-full pl-8 pr-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="p-6 border-t border-warm-100 flex justify-end gap-3">
              <button
                onClick={() => setShowPayRateModal(false)}
                className="px-4 py-2 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePayRate}
                disabled={isSaving || !newPayRate}
                className="btn-primary disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Update Pay Rate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Override Activate Modal */}
      {showOverrideModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-y-auto max-h-[90vh]">
            <div className="p-6 border-b border-warm-100">
              <h2 className="text-xl font-semibold text-warm-900">Override Activate Faculty?</h2>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-warm-700">
                This will make <strong>{faculty.name}</strong> bookable to classes immediately,
                even though their onboarding is incomplete.
              </p>

              {(() => {
                const incomplete = progress.steps.filter((s) => !s.completed);
                if (incomplete.length === 0) {
                  return (
                    <p className="text-sm text-warm-600">
                      All onboarding steps are complete — you can also use the regular Approve flow.
                    </p>
                  );
                }
                return (
                  <div>
                    <p className="text-sm text-warm-700 mb-2">The following steps are not yet complete:</p>
                    <ul className="list-disc list-inside text-sm text-warm-700 space-y-1">
                      {incomplete.map((s) => (
                        <li key={s.key}>{s.name}</li>
                      ))}
                    </ul>
                  </div>
                );
              })()}

              <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
                <p className="text-sm text-warning-700">
                  Note: this faculty will be bookable, but cannot receive payment until they
                  complete Stripe setup.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-warm-100 flex justify-end gap-3">
              <button
                onClick={() => setShowOverrideModal(false)}
                disabled={isSaving}
                className="px-4 py-2 text-warm-700 hover:bg-warm-100 rounded-lg disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleOverrideActivate}
                disabled={isSaving}
                className="px-4 py-2 bg-warning-600 text-white rounded-lg hover:bg-warning-700 disabled:opacity-50 transition-colors font-medium"
              >
                {isSaving ? 'Activating...' : 'Override Activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Show Modal */}
      {showAddShowModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-y-auto max-h-[90vh]">
            <div className="p-6 border-b border-warm-100">
              <h2 className="text-xl font-semibold text-warm-900">Add Show Certification</h2>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-warm-700 mb-1">
                Select Show
              </label>
              <select
                value={selectedShowId}
                onChange={(e) => setSelectedShowId(e.target.value)}
                className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select a show...</option>
                {availableShows.map((show) => (
                  <option key={show.id} value={show.id}>
                    {show.name}
                  </option>
                ))}
              </select>
              {availableShows.length === 0 && (
                <p className="mt-2 text-sm text-warm-500">
                  All available shows have been assigned.
                </p>
              )}
            </div>
            <div className="p-6 border-t border-warm-100 flex justify-end gap-3">
              <button
                onClick={() => setShowAddShowModal(false)}
                className="px-4 py-2 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddShow}
                disabled={isSaving || !selectedShowId}
                className="btn-primary disabled:opacity-50"
              >
                {isSaving ? 'Adding...' : 'Add Certification'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          confirmVariant={confirmAction.variant}
          onConfirm={async () => {
            await confirmAction.action();
            setConfirmAction(null);
          }}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}
